'use client';
// Network Launch — the operator creates the founder wallet and the genesis block
// mints the founder allocation directly into it. The first wallet starts the chain.
import { useState } from 'react';
import { Rocket, ShieldCheck, Network, KeyRound, TriangleAlert, Check, Copy, Loader2, Eye, EyeOff, FlaskConical, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { post, Mono, CopyBtn, fmtAmt } from './shared';
import { useSession } from './session';
import type { Net, NetworkInfo } from './types';

interface LaunchResponse {
  launched?: boolean;
  wallet?: { id: string; name: string; address: string; publicKey: string; kind: string };
  seed?: string;
  provisioning?: { step: string; detail: string; ok: boolean }[];
  provisionError?: string | null;
  message?: string;
  error?: string;
}

export function LaunchScreen({
  net,
  networks,
  onLaunched,
  onSwitch,
}: {
  net: Net;
  networks: Record<Net, NetworkInfo> | null;
  onLaunched: (net: Net) => void;
  onSwitch: (net: Net) => void;
}) {
  const [step, setStep] = useState<'choose' | 'create' | 'seed'>('choose');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<false | 'copied' | 'downloaded'>(false);
  const [result, setResult] = useState<LaunchResponse | null>(null);
  const { login } = useSession();

  const info = networks?.[net];
  const cfg = {
    mainnet: { icon: Building2, tone: 'emerald', label: 'Mainnet', chainId: 1, desc: 'The permanent ledger of record. Founder allocation: 20,000,000 AMAN. No faucet — real scarcity from block zero.' },
    testnet: { icon: FlaskConical, tone: 'sky', label: 'Testnet', chainId: 2, desc: 'Identical protocol with a free faucet — rehearse token deployments here before touching Mainnet.' },
  }[net];

  const canSubmit = name.trim().length >= 2 && password.length >= 6 && password === confirm;

  const launch = async () => {
    setBusy(true);
    try {
      const res = await post<LaunchResponse>('/api/launch', { net, walletName: name, password });
      if (res.error) { toast.error(res.error); return; }
      setResult(res);
      setStep('seed');
      const steps = res.provisioning?.length ?? 0;
      toast.success(`${cfg.label} is fully live`, {
        description: res.provisionError
          ? `Founder allocation minted — automatic provisioning hit: ${res.provisionError}`
          : `Founder allocation minted + ${steps} provisioning steps: assets, pools and AI agents are ready.`,
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Launch failed');
    } finally {
      setBusy(false);
    }
  };

  const downloadSeed = () => {
    if (!result?.seed) return;
    const blob = new Blob(
      [`AmanChain ${cfg.label} founder seed\nAddress: ${result.wallet?.address}\nSeed: ${result.seed}\nKeep this file offline and private — whoever holds the seed owns the wallet.\n`],
      { type: 'text/plain' },
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `aman-${net}-founder-seed.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
    setSaved(s => s || 'downloaded');
  };

  const copySeed = async () => {
    if (!result?.seed) return;
    try { await navigator.clipboard.writeText(result.seed); } catch { /* ignore */ }
    setSaved(s => s || 'copied');
  };

  const ToneIcon = cfg.icon;

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-10 overflow-hidden">
      <div className="absolute inset-0 grid-bg pointer-events-none" aria-hidden />
      <div className="relative w-full max-w-2xl">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 rounded-full ring-1 ring-cyan-400/30 bg-cyan-500/10 text-cyan-300 px-3 py-1 text-xs font-bold mb-5">
            <ShieldCheck className="size-3.5" />
            Post-quantum security from block zero
          </div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">
            Launch <span className="grad-text">AmanChain</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-lg mx-auto leading-relaxed">
            Create the founder (admin) wallet that starts the network — the first address on the chain is yours.
            The moment it is created, the node provisions everything automatically: USDT · USDC · BTC · ETH · SOL · BNB
            with live AMM pools, plus the built-in AI agents with x402-paid services. Real proof-of-work, real transactions — ready from the first click.
          </p>
        </div>

        {step === 'choose' && (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {(['mainnet', 'testnet'] as Net[]).map(n => {
                const c = {
                  mainnet: { icon: Building2, tone: 'emerald', label: 'Mainnet', desc: 'The permanent ledger of record — 20,000,000 AMAN founder allocation, no faucet, real scarcity.' },
                  testnet: { icon: FlaskConical, tone: 'sky', label: 'Testnet', desc: 'Identical protocol, free faucet — the rehearsal space for token deployments.' },
                }[n];
                const launched = networks?.[n]?.launched;
                const Icon = c.icon;
                const active = net === n;
                return (
                  <button
                    key={n}
                    onClick={() => onSwitch(n)}
                    className={`glass glass-hover p-5 text-left ring-1 transition ${active ? 'ring-primary/60' : 'ring-transparent'}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className={`rounded-xl p-2.5 ring-1 ${n === 'mainnet' ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/25' : 'bg-sky-500/10 text-sky-400 ring-sky-500/25'}`}>
                        <Icon className="size-5" />
                      </div>
                      {launched ? (
                        <span className="text-[0.65rem] font-bold rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30 px-2 py-0.5">LIVE</span>
                      ) : (
                        <span className="text-[0.65rem] font-bold rounded-full bg-amber-500/10 text-amber-400 ring-1 ring-amber-500/30 px-2 py-0.5">NOT LAUNCHED</span>
                      )}
                    </div>
                    <div className="font-bold">AmanChain {c.label}</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{c.desc}</div>
                    <div className="mt-3">
                      <Mono>Chain-ID {n === 'mainnet' ? 1 : 2}</Mono>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="glass p-5">
              <div className="flex items-start gap-3">
                <ToneIcon className={`size-5 mt-0.5 ${net === 'mainnet' ? 'text-emerald-400' : 'text-sky-400'}`} />
                <div className="flex-1">
                  <div className="text-sm font-bold">You are launching AmanChain {cfg.label}</div>
                  <p className="text-xs text-muted-foreground mt-1">{cfg.desc}</p>
                  <button
                    onClick={() => setStep('create')}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 transition"
                  >
                    <Rocket className="size-4" /> Create the Founder Wallet
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'create' && (
          <div className="glass p-6">
            <div className="flex items-center gap-2.5 mb-5">
              <KeyRound className="size-5 text-primary" />
              <div>
                <div className="font-bold">Founder wallet — AmanChain {cfg.label}</div>
                <div className="text-xs text-muted-foreground">The genesis block will mint the founder supply into this wallet.</div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block" htmlFor="fw-name">Wallet name</label>
                <input
                  id="fw-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Genesis Vault"
                  maxLength={24}
                  className="w-full rounded-lg bg-background/60 ring-1 ring-input px-3.5 py-2.5 text-sm outline-none focus:ring-primary/60"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block" htmlFor="fw-pass">Password <span className="font-normal">(encrypts the wallet seed — AES-256-GCM)</span></label>
                <div className="relative">
                  <input
                    id="fw-pass"
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    className="w-full rounded-lg bg-background/60 ring-1 ring-input px-3.5 py-2.5 text-sm outline-none focus:ring-primary/60"
                  />
                  <button type="button" onClick={() => setShowPw(s => !s)} aria-label={showPw ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1.5 block" htmlFor="fw-confirm">Confirm password</label>
                <input
                  id="fw-confirm"
                  type={showPw ? 'text' : 'password'}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat the password"
                  className="w-full rounded-lg bg-background/60 ring-1 ring-input px-3.5 py-2.5 text-sm outline-none focus:ring-primary/60"
                />
                {confirm.length > 0 && password !== confirm && (
                  <div className="text-[0.7rem] text-rose-400 mt-1.5">Passwords do not match</div>
                )}
              </div>
              <div className="flex items-center justify-between pt-2">
                <button onClick={() => setStep('choose')} className="text-xs text-muted-foreground hover:text-foreground transition">← Back to networks</button>
                <button
                  disabled={!canSubmit || busy}
                  onClick={launch}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {busy ? <Loader2 className="size-4 animate-spin" /> : <Rocket className="size-4" />}
                  {busy ? 'Launching — genesis, essentials & AI agents…' : 'Create First Wallet & Launch'}
                </button>
              </div>
            </div>
          </div>
        )}

        {step === 'seed' && result?.seed && (
          <div className="glass p-6 ring-1 ring-amber-500/25">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/30 p-2.5 text-amber-400">
                <TriangleAlert className="size-5" />
              </div>
              <div>
                <div className="font-bold">Your founder seed — shown only once</div>
                <div className="text-xs text-muted-foreground">This 64-hex master seed derives every key in the wallet. There is no recovery without it.</div>
              </div>
            </div>
            <div className="rounded-lg bg-background/70 ring-1 ring-input p-4">
              <Mono className="text-xs break-all leading-relaxed text-amber-300">{result.seed}</Mono>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button onClick={copySeed} className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-input px-3 py-2 text-xs font-bold hover:ring-primary/50 transition">
                <Copy className="size-3.5" /> Copy seed
              </button>
              <button onClick={downloadSeed} className="inline-flex items-center gap-1.5 rounded-lg ring-1 ring-input px-3 py-2 text-xs font-bold hover:ring-primary/50 transition">
                ⬇ Download as file
              </button>
            </div>
            <label className="flex items-start gap-2.5 mt-5 cursor-pointer select-none">
              <input type="checkbox" checked={saved !== false} onChange={e => e.target.checked && setSaved('copied')} className="mt-0.5 accent-emerald-500" />
              <span className="text-xs text-muted-foreground">I have stored the seed somewhere safe and understand it cannot be recovered later.</span>
            </label>

            {result.provisioning && result.provisioning.length > 0 && (
              <div className="mt-5 rounded-xl ring-1 ring-emerald-500/25 bg-emerald-500/5 p-4">
                <div className="text-xs font-black text-emerald-400 flex items-center gap-1.5 mb-2">
                  <Rocket className="size-3.5" /> Provisioned automatically with your launch — all on-chain
                </div>
                <div className="space-y-1 max-h-36 overflow-y-auto">
                  {result.provisioning.map((s, i) => (
                    <div key={`${s.step}-${i}`} className="flex items-start gap-1.5 text-[0.65rem] text-muted-foreground">
                      <Check className="size-3 text-emerald-400 shrink-0 mt-0.5" />
                      <span><b className="text-foreground/80">{s.step}</b> — {s.detail}</span>
                    </div>
                  ))}
                </div>
                {result.provisionError && (
                  <div className="text-[0.65rem] text-amber-400 mt-2">Provisioning notice: {result.provisionError}</div>
                )}
              </div>
            )}
            <button
              disabled={saved === false}
              onClick={() => {
                if (result?.wallet?.id) {
                  login({ walletId: result.wallet.id, name: result.wallet.name, address: result.wallet.address, kind: result.wallet.kind, password });
                  toast.success('Founder session opened — the Admin console is available to you');
                }
                onLaunched(net);
              }}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-black text-primary-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              <Check className="size-4" /> Enter {cfg.label}
            </button>
          </div>
        )}

        {/* Status of both networks */}
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {(['mainnet', 'testnet'] as Net[]).map(n => {
            const ni = networks?.[n];
            return (
              <div key={n} className="glass p-4 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Network className={`size-4 ${n === 'mainnet' ? 'text-emerald-400' : 'text-sky-400'}`} />
                  <div>
                    <div className="text-xs font-bold">AmanChain {n === 'mainnet' ? 'Mainnet' : 'Testnet'}</div>
                    <div className="text-[0.65rem] text-muted-foreground">
                      {ni?.launched
                        ? <>Height {fmtAmt(ni.height, 0)} · {fmtAmt(ni.tokens, 0)} tokens</>
                        : 'Awaiting founder launch'}
                    </div>
                  </div>
                </div>
                {ni?.launched
                  ? <span className="text-[0.62rem] font-bold rounded-full bg-emerald-500/10 text-emerald-400 ring-1 ring-emerald-500/30 px-2 py-0.5">LIVE</span>
                  : <span className="text-[0.62rem] font-bold rounded-full bg-muted text-muted-foreground ring-1 ring-border px-2 py-0.5">OFFLINE</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
